import type { ContestStatus } from '@s2w/types';
import type { ContestLifecycleState, DerivedSettlementMetadata } from '@s2w/core';
import { adminApiClient, type AdminApiClient } from './client';

export type { ContestStatus };

export interface ContestName {
  en: string;
}

export interface ContestSummary {
  id: string;
  name: ContestName;
  superTitle: string | null;
  subTitle: string | null;
  description: string | null;
  status: ContestStatus;
  statusChangedAt: string;
  pausedFromStatus: ContestStatus | null;
  date: string;
  opensAt: string;
  closesAt: string | null;
  adapterConfig: Record<string, unknown> | null;
  /*
   * Always sent by the API since CAE-1566. Optional here, like `ContestReel.kind`, so fixtures
   * written before it need no change; a Contest without a Reel count reads as three.
   */
  sportId?: string;
  reelCount?: number;
  /** On the list, the Sport's id and name; on the detail, also the Reel mix it asks for. */
  sport?: ContestSport;
  createdAt: string;
  /**
   * How many Entries the Contest holds. **Required, where the rest of this
   * payload's extras are optional** (CAE-1587).
   *
   * It was optional while only `serialiseContestListItem` sent it, and the
   * Contest Overview's cancel confirmation — the one screen where this number
   * decides the Operator's answer — read it behind a `?? 0` and so told them
   * every Contest had no Entries at risk. `serialiseDetail` now sends it too,
   * which is what lets this stop being optional: a screen that forgets it is a
   * type error rather than a confident zero.
   */
  entryCount: number;
  prizeDeliveryProgressPercent?: number;
}

export interface ContestSport {
  id: string;
  displayName: string;
  defaultReelCount?: number;
  requiredGameReels?: number;
  requiredPlayerReels?: number;
}

export interface ContestFixture {
  id: string;
  contestId: string;
  externalId: string;
  name: string;
  displayNameOverride: string | null;
  homeTeam: string;
  awayTeam: string;
  /** Each side's club in the Sport's registry, frozen when the fixture joined the Contest; null when none matched (CAE-1610). */
  homeClubAbbreviation: string | null;
  awayClubAbbreviation: string | null;
  startTime: string;
  status: 'scheduled' | 'live' | 'finished' | 'settled';
  createdAt: string;
}

/**
 * An attached fixture as the contest **detail** carries it (CAE-1573).
 *
 * The two extra facts are the catalogue's, not the contest's — a fixture the
 * feed has since dropped keeps its row and answers `null` for both — so they
 * ride on the detail payload alone and not on `ContestFixture`, which is also
 * what the attach and rename endpoints return.
 */
export interface ContestDetailFixture extends ContestFixture {
  competitionName: string | null;
  sportCode: string | null;
  /**
   * What the catalogue currently holds for this fixture, counted server-side.
   *
   * The Fixtures section used to total these in the browser, off
   * `useFixtureMarketCounts` plus a per-market outcome fan-out. One real World
   * Cup fixture carries 141 markets, which made that 282 requests on a single
   * tab open; the backend already joins the catalogue here for the competition,
   * so it counts too.
   */
  marketCount: number;
  outcomeCount: number;
}

export type ManualScoreResult = 'WIN' | 'LOSE' | 'VOID';

export interface ContestReelOption {
  id: string;
  reelId: string;
  contestFixtureId: string;
  externalOutcomeId: string;
  externalMarketId: string | null;
  outcomeName: string;
  marketName: string | null;
  displayNameOverride: string | null;
  /**
   * The proposition frozen when the option was added, for a pick settled by
   * derivation rather than an explicit result: an over/under's direction and
   * line, or a spread's side and signed handicap. Null for every other pick.
   */
  settlementMetadata: DerivedSettlementMetadata | null;
  assetId: string | null;
  /**
   * Who the pick is about, frozen when the option was added (GL-12; CAE-1610):
   * the club by its abbreviation, and for a Player Market the player's name
   * and jersey number as the feed's roster had them. Null when the pick has no
   * club (a game total) or the feed could not say.
   */
  clubAbbreviation: string | null;
  playerName: string | null;
  jerseyNumber: string | null;
  /** The Operator's club or number where the feed's was missing or wrong; the Game draws `override ?? frozen`. */
  clubAbbreviationOverride: string | null;
  jerseyNumberOverride: string | null;
  enabled: boolean;
  position: number;
  settlement: {
    status: 'awaiting' | 'settled';
    /** The result in force: the latest correction where there is one (CAE-1696). */
    result?: 'WIN' | 'LOSE' | 'VOID';
    receivedAt?: string;
    /**
     * Set only when a correction stands: the first result it replaced, and
     * when it arrived. A correction applies itself now, so it never reaches
     * the settlement-corrections panel — this row is the only place an
     * Operator is told the result they are looking at is not the first one.
     */
    correctedFrom?: 'WIN' | 'LOSE' | 'VOID';
    correctedAt?: string;
  };
}

/** The Operator's club or number for an option; a key left out is unchanged, null clears it (CAE-1610). */
export interface ReelOptionParticipantPayload {
  clubAbbreviationOverride?: string | null;
  jerseyNumberOverride?: string | null;
}

/**
 * A Reel Option as the contest **detail** carries it (CAE-1574).
 *
 * The odds and the suspension are the catalogue's answer, not the contest's, so
 * they ride on the detail alone — the five option-mutation endpoints answer from
 * writes that never join the catalogue and still return `ContestReelOption`.
 */
export interface ContestDetailReelOption extends ContestReelOption {
  /** `price.d` on the catalogue outcome. `null` when the feed no longer carries it. */
  oddsDecimal: number | null;
  /**
   * `price.f`, exactly as the feed gave it — the odds column renders this
   * verbatim and never derives a fraction from the decimal.
   */
  oddsFractional: string | null;
  /**
   * The book has taken this selection off. `null` when the catalogue holds no
   * outcome for it at all, which is the absence of a claim rather than a live
   * one — the Status column draws that as unknown, not as Active.
   */
  suspended: boolean | null;
  /**
   * The figure the feed resulted for the option's market (a total, or a spread's
   * home margin), once the market is confirmed; null before then, while the
   * figure can still change (CAE-1699).
   */
  stagedFigure: number | null;
  /**
   * The images the option's effective club lends it (GL-12; CAE-1610), and
   * their read URLs. On the detail only, like the odds: a club's image can
   * change after the option froze its club. Null without a club or an image.
   * The avatar is the main picture of a team pick (CAE-1671); the logo stands in
   * while the club has none.
   */
  clubLogoAssetId: string | null;
  clubJerseyAssetId: string | null;
  clubAvatarAssetId: string | null;
  clubLogoUrl: string | null;
  clubJerseyUrl: string | null;
  clubAvatarUrl: string | null;
}

/** Game Market Reel or Player Market Reel (GL-08). */
export type ReelKind = 'game' | 'player';

/**
 * The kind a Reel write sends: none for a Reel that exists, which then keeps its own, and for
 * one the write creates, the kind the Operator chose for it, else Game Market (GL-08).
 */
export const reelKindForWrite = (
  reel: { kind?: ReelKind } | null | undefined,
  chosen?: ReelKind,
): { kind?: ReelKind } => (reel ? {} : { kind: chosen ?? 'game' });

/**
 * The kind a new Reel starts as, from its Sport's required mix: the first `requiredGameReels`
 * Reels are Game Market Reels and the rest Player Market Reels. A Sport that asks for no mix,
 * as Soccer does, starts every Reel as a Game Market Reel.
 */
export const defaultReelKind = (
  reelNumber: number,
  sport: { requiredGameReels?: number; requiredPlayerReels?: number } | undefined,
): ReelKind => {
  const gameReels = sport?.requiredGameReels ?? 0;
  const playerReels = sport?.requiredPlayerReels ?? 0;
  if (gameReels + playerReels === 0) return 'game';
  return reelNumber <= gameReels ? 'game' : 'player';
};

export interface ContestReel {
  id: string;
  contestId: string;
  reelNumber: number;
  /**
   * Always sent by the API. Optional here so fixtures in the screens the admin redesign
   * (CAE-1536) is rebuilding need no change before the builder reads it (S2W-D06-02).
   */
  kind?: ReelKind;
  label: string | null;
  options: ContestReelOption[];
}

export interface ContestDetailReel extends Omit<ContestReel, 'options'> {
  options: ContestDetailReelOption[];
}

export interface PrizeTier {
  id: string;
  correctCount: number;
  segmentRef: string;
  prizeType: string;
  displayValue: string;
}

export interface ConfiguredPrizeTier extends PrizeTier {
  adapterConfig: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface ContestEntry {
  id: string;
  userId: string;
  userExternalId: string | null;
  totalCorrect: number | null;
  prizeTier: PrizeTier | null;
  submittedAt: string;
  settledAt: string | null;
}

export interface PrizeAward {
  id: string;
  entryId: string;
  userId: string;
  prizeTier: PrizeTier;
  status: 'awaiting_confirmation' | 'pending' | 'processing' | 'completed' | 'error';
  deliveryRef: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface LobbyImage {
  assetId: string;
  name: string;
  previewUrl: string | null;
}

/**
 * The close publish would set, from the Contest as it now stands (CAE-1691).
 * With rolling close, the Reels whose enabled options miss the last game,
 * each of which closes the Contest before that game.
 */
export interface ContestClosurePreview {
  rollingClose: boolean;
  closesAt: string | null;
  reelsWithoutLastGame: number[];
}

export interface ContestDetail {
  /** True when at least one prize award has completed delivery; locks campaignId and round. */
  adapterConfigFieldsLocked: boolean;
  closure: ContestClosurePreview;
  contest: ContestSummary;
  reels: ContestDetailReel[];
  fixtures: ContestDetailFixture[];
  entries: ContestEntry[];
  prizeAwards: PrizeAward[];
  prizeTiers: ConfiguredPrizeTier[];
  lobbyImages: LobbyImage[];
}

export interface CreateContestPayload {
  name: ContestName;
  sportId: string;
  /** 3 or 4; defaults to the Sport's default Reel count. */
  reelCount?: number;
  superTitle?: string | null;
  subTitle?: string | null;
  description?: string | null;
  date?: string;
  opensAt: string;
  adapterConfig?: Record<string, unknown>;
}

export interface UpdateContestPayload {
  name?: ContestName;
  superTitle?: string | null;
  subTitle?: string | null;
  description?: string | null;
  date?: string;
  opensAt?: string;
  adapterConfig?: Record<string, unknown>;
  /** Accepted while the Contest is a draft only; sending either afterwards is refused (409). */
  sportId?: string;
  reelCount?: number;
}

export interface CreatePrizeTierPayload {
  correctCount: number;
  segmentRef: string;
  prizeType: string;
  displayValue: string;
  adapterConfig: Record<string, unknown>;
}

export interface UpdatePrizeTierPayload {
  correctCount?: number;
  segmentRef?: string;
  prizeType?: string;
  displayValue?: string;
  adapterConfig?: Record<string, unknown>;
}

const omitUndefined = <T extends object>(payload: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Partial<T>;

export interface CreateReelOptionFromCataloguePayload {
  fixture: {
    externalId: string;
    name: string;
    homeTeam: string;
    awayTeam: string;
    startTime: string;
    status: ContestFixture['status'];
  };
  externalMarketId: string | null;
  marketName: string | null;
  externalOutcomeId: string;
  outcomeName: string;
  displayNameOverride: string | null;
}

/** What the Contests list narrows the board by, and the server narrows with it. */
export interface ContestListFilters {
  status?: ContestStatus;
  /**
   * The lifecycle state a chip stands for.
   *
   * Not `ContestFilterState`: `all` is the chip row's own value, not a state the
   * API knows, and the screen sends `undefined` for it.
   */
  state?: ContestLifecycleState;
  search?: string;
  limit?: number;
  offset?: number;
}

/**
 * The longest query the search endpoints will take.
 *
 * The same 200 the route's TypeBox schema enforces
 * (`apps/backend/src/admin/contests/routes.ts`), and capped here rather than
 * only validated there: the search box has no length limit of its own, so a
 * pasted paragraph would otherwise come back a 400 and reach the operator as
 * "contests could not be loaded". Nothing is lost — a 200-character contest
 * name does not exist, so the truncated query finds what the whole one would.
 */
export const CONTEST_SEARCH_MAX_LENGTH = 200;

export type ContestCounts = Record<ContestLifecycleState, number>;

export interface ContestCountsResponse {
  counts: ContestCounts;
  /** Every contest on the board — what the All chip reads. */
  total: number;
}

export interface ContestsApi {
  list: (filters?: ContestListFilters) => Promise<{ items: ContestSummary[]; total: number }>;
  /**
   * The chip counts, resolved server-side.
   *
   * A separate request from `list` rather than a field on it: the counts
   * describe the whole board and do not change when the operator turns a page
   * or picks a chip, so folding them into the list response would recount on
   * every one of those. Two queries, two cache keys, one invalidation.
   */
  counts: (filters?: { search?: string }) => Promise<ContestCountsResponse>;
  create: (payload: CreateContestPayload) => Promise<ContestSummary>;
  update: (id: string, payload: UpdateContestPayload) => Promise<ContestSummary>;
  get: (id: string) => Promise<ContestDetail>;
  publish: (id: string) => Promise<ContestSummary>;
  cancel: (id: string) => Promise<ContestSummary>;
  pause: (id: string) => Promise<ContestSummary>;
  resume: (id: string) => Promise<ContestSummary>;
  setLobbyImages: (contestId: string, assetIds: string[]) => Promise<void>;
  upsertReel: (
    contestId: string,
    reelNumber: number,
    payload: { kind?: ReelKind; label?: string | null },
  ) => Promise<ContestReel>;
  createReelOptionFromCatalogue: (
    contestId: string,
    reelNumber: number,
    payload: CreateReelOptionFromCataloguePayload,
  ) => Promise<ContestReelOption>;
  updateReelOptionDisplayNameOverride: (
    contestId: string,
    reelNumber: number,
    optionId: string,
    displayNameOverride: string | null,
  ) => Promise<ContestReelOption>;
  removeReelOption: (contestId: string, reelNumber: number, optionId: string) => Promise<void>;
  updateReelOptionAsset: (
    contestId: string,
    reelNumber: number,
    optionId: string,
    assetId: string | null,
  ) => Promise<ContestReelOption>;
  /** Sets or clears the Operator's club and jersey number on an option (CAE-1610). */
  updateReelOptionParticipant: (
    contestId: string,
    reelNumber: number,
    optionId: string,
    payload: ReelOptionParticipantPayload,
  ) => Promise<ContestReelOption>;
  updateReelOptionEnabled: (
    contestId: string,
    reelNumber: number,
    optionId: string,
    enabled: boolean,
  ) => Promise<ContestReelOption>;
  reorderReelOption: (
    contestId: string,
    reelNumber: number,
    optionId: string,
    position: number,
  ) => Promise<ContestReelOption>;
  scoreReelOption: (reelOptionId: string, result: ManualScoreResult) => Promise<void>;
  addFixtures: (
    contestId: string,
    fixtureExternalIds: string[],
  ) => Promise<{ fixtures: ContestFixture[] }>;
  updateFixtureDisplayNameOverride: (
    contestId: string,
    fixtureId: string,
    displayNameOverride: string | null,
  ) => Promise<ContestFixture>;
  removeFixture: (contestId: string, contestFixtureId: string) => Promise<void>;
  listPrizeTiers: (contestId: string) => Promise<ConfiguredPrizeTier[]>;
  createPrizeTier: (
    contestId: string,
    payload: CreatePrizeTierPayload,
  ) => Promise<ConfiguredPrizeTier>;
  updatePrizeTier: (
    contestId: string,
    prizeTierId: string,
    payload: UpdatePrizeTierPayload,
  ) => Promise<ConfiguredPrizeTier>;
  deletePrizeTier: (contestId: string, prizeTierId: string) => Promise<void>;
}
type AddFixturesResponse = { fixtures: ContestFixture[] };

const isContestFixture = (value: unknown): value is ContestFixture => {
  if (!value || typeof value !== 'object') return false;
  const fixture = value as Record<string, unknown>;
  return (
    typeof fixture.id === 'string' &&
    typeof fixture.contestId === 'string' &&
    typeof fixture.externalId === 'string' &&
    typeof fixture.name === 'string' &&
    (fixture.displayNameOverride === null || typeof fixture.displayNameOverride === 'string') &&
    typeof fixture.homeTeam === 'string' &&
    typeof fixture.awayTeam === 'string' &&
    typeof fixture.startTime === 'string' &&
    (fixture.status === 'scheduled' ||
      fixture.status === 'live' ||
      fixture.status === 'finished' ||
      fixture.status === 'settled') &&
    typeof fixture.createdAt === 'string'
  );
};

const assertAddFixturesResponse: (value: unknown) => asserts value is AddFixturesResponse = (
  value,
) => {
  if (!value || typeof value !== 'object' || !('fixtures' in value)) {
    throw new Error('Invalid addFixtures response: expected an object with a fixtures array.');
  }
  const fixtures = (value as { fixtures?: unknown }).fixtures;
  if (!Array.isArray(fixtures) || !fixtures.every(isContestFixture)) {
    throw new Error('Invalid addFixtures response: fixtures payload shape is not valid.');
  }
};

export const createContestsApi = (client: AdminApiClient = adminApiClient): ContestsApi => ({
  list: async (filters) => {
    const params = new URLSearchParams();
    if (filters?.status) {
      params.set('status', filters.status);
    }
    if (filters?.state) {
      params.set('state', filters.state);
    }
    // A blank box is no filter. Sent as an empty `search` the server would match
    // it against everything, which is the same answer by a longer route.
    const search = filters?.search?.trim();
    if (search) {
      params.set('search', search.slice(0, CONTEST_SEARCH_MAX_LENGTH));
    }
    if (filters?.limit != null) {
      params.set('limit', String(filters.limit));
    }
    if (filters?.offset != null) {
      params.set('offset', String(filters.offset));
    }
    const qs = params.toString();
    return client.request<{ items: ContestSummary[]; total: number }>(
      `/admin/contests${qs ? `?${qs}` : ''}`,
    );
  },
  counts: async (filters) => {
    const query = filters?.search?.trim().slice(0, CONTEST_SEARCH_MAX_LENGTH);
    return client.request<ContestCountsResponse>(
      `/admin/contests/counts${query ? `?search=${encodeURIComponent(query)}` : ''}`,
    );
  },
  create: async (payload) =>
    client.request<ContestSummary>('/admin/contests', { method: 'POST', body: payload }),
  update: async (id, payload) =>
    client.request<ContestSummary>(`/admin/contests/${id}`, {
      method: 'PATCH',
      body: omitUndefined(payload),
    }),
  get: async (id) => client.request<ContestDetail>(`/admin/contests/${id}`),
  publish: async (id) =>
    client.request<ContestSummary>(`/admin/contests/${id}/publish`, { method: 'POST' }),
  cancel: async (id) =>
    client.request<ContestSummary>(`/admin/contests/${id}/cancel`, { method: 'POST' }),
  pause: async (id) =>
    client.request<ContestSummary>(`/admin/contests/${id}/pause`, { method: 'POST' }),
  resume: async (id) =>
    client.request<ContestSummary>(`/admin/contests/${id}/resume`, { method: 'POST' }),
  setLobbyImages: async (contestId, assetIds) => {
    await client.request<void>(`/admin/contests/${contestId}/lobby-images`, {
      method: 'PUT',
      body: { assetIds },
    });
  },
  upsertReel: async (contestId, reelNumber, payload) =>
    client.request<ContestReel>(`/admin/contests/${contestId}/reels/${reelNumber}`, {
      method: 'PUT',
      body: payload,
    }),
  createReelOptionFromCatalogue: async (contestId, reelNumber, payload) =>
    client.request<ContestReelOption>(`/admin/contests/${contestId}/reels/${reelNumber}/options`, {
      method: 'POST',
      body: payload,
    }),
  updateReelOptionDisplayNameOverride: async (
    contestId,
    reelNumber,
    optionId,
    displayNameOverride,
  ) =>
    client.request<ContestReelOption>(
      `/admin/contests/${contestId}/reels/${reelNumber}/options/${optionId}/display-name-override`,
      {
        method: 'PATCH',
        body: { displayNameOverride },
      },
    ),
  removeReelOption: async (contestId, reelNumber, optionId) => {
    await client.request(`/admin/contests/${contestId}/reels/${reelNumber}/options/${optionId}`, {
      method: 'DELETE',
    });
  },
  updateReelOptionAsset: async (contestId, reelNumber, optionId, assetId) =>
    client.request<ContestReelOption>(
      `/admin/contests/${contestId}/reels/${reelNumber}/options/${optionId}/asset`,
      {
        method: 'PATCH',
        body: { assetId },
      },
    ),
  updateReelOptionParticipant: async (contestId, reelNumber, optionId, payload) =>
    client.request<ContestReelOption>(
      `/admin/contests/${contestId}/reels/${reelNumber}/options/${optionId}/participant`,
      {
        method: 'PATCH',
        body: payload,
      },
    ),
  updateReelOptionEnabled: async (contestId, reelNumber, optionId, enabled) =>
    client.request<ContestReelOption>(
      `/admin/contests/${contestId}/reels/${reelNumber}/options/${optionId}/enabled`,
      {
        method: 'PATCH',
        body: { enabled },
      },
    ),
  reorderReelOption: async (contestId, reelNumber, optionId, position) =>
    client.request<ContestReelOption>(
      `/admin/contests/${contestId}/reels/${reelNumber}/options/${optionId}/position`,
      {
        method: 'PATCH',
        body: { position },
      },
    ),
  scoreReelOption: async (reelOptionId, result) => {
    await client.request(`/admin/reel-options/${reelOptionId}/score`, {
      method: 'POST',
      body: { result },
    });
  },
  addFixtures: async (contestId, fixtureExternalIds): Promise<AddFixturesResponse> => {
    const response = await client.request<unknown>(`/admin/contests/${contestId}/fixtures`, {
      method: 'POST',
      body: { fixtureExternalIds },
      expectedStatus: 201,
    });
    assertAddFixturesResponse(response);
    return response;
  },
  updateFixtureDisplayNameOverride: async (contestId, fixtureId, displayNameOverride) =>
    client.request<ContestFixture>(`/admin/contests/${contestId}/fixtures/${fixtureId}`, {
      method: 'PATCH',
      body: { displayNameOverride },
    }),
  removeFixture: async (contestId, contestFixtureId) => {
    await client.request(`/admin/contests/${contestId}/fixtures/${contestFixtureId}`, {
      method: 'DELETE',
    });
  },
  listPrizeTiers: async (contestId) =>
    client.request<ConfiguredPrizeTier[]>(`/admin/contests/${contestId}/prize-tiers`),
  createPrizeTier: async (contestId, payload) =>
    client.request<ConfiguredPrizeTier>(`/admin/contests/${contestId}/prize-tiers`, {
      method: 'POST',
      body: payload,
    }),
  updatePrizeTier: async (contestId, prizeTierId, payload) =>
    client.request<ConfiguredPrizeTier>(`/admin/contests/${contestId}/prize-tiers/${prizeTierId}`, {
      method: 'PATCH',
      body: omitUndefined(payload),
    }),
  deletePrizeTier: async (contestId, prizeTierId) => {
    await client.request(`/admin/contests/${contestId}/prize-tiers/${prizeTierId}`, {
      method: 'DELETE',
    });
  },
});

export const contestsApi = createContestsApi();
